import { Input } from "antd";
import { VyneReactInputANTDBase } from "./VyneReactInputANTDBase";
import { isValidPhone, patchUpPhone } from "utils/phone";
import { useState } from "react";
import PhoneInput from "react-phone-number-input/input";

interface VynePhoneProps {
    readonly initialPhone: string;
    readonly onChange?: (newValue: string) => void;
    readonly onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    readonly required?: boolean;
}

function useVynePhoneData({ initialPhone, onChange }: VynePhoneProps) {
    const isInitialValid = isValidPhone(initialPhone);
    const [shouldUsePhoneInput, setShouldUsePhoneInput] = useState(isInitialValid);

    // handle updating the phone number
    const updatePhone = (newPhone: string) => {
        let finalPhone = newPhone;
        const patchedUpPhone = patchUpPhone(newPhone) ?? ""; // format the phone for validation and use by PhoneInput

        if (!shouldUsePhoneInput && isValidPhone(patchedUpPhone)) {
            finalPhone = patchedUpPhone;
            setShouldUsePhoneInput(true);
        }

        // call the `onChange` prop if it exists, required for AntD custom inputs
        if (onChange) {
            onChange(finalPhone);
        }
    };

    return {
        updatePhone,
        shouldUsePhoneInput,
    };
}

/**
 * Phone number input that allows completely custom data, falling back to a regular text input if the data cannot be
 * coerced to a phone number.
 */
// NOTE: AntD passes additional props that are NOT described in CustomPhoneProps that need to be passed down to the
// underlying input component. Make sure arbitrary props are supported!
export const VynePhoneInput = ({ initialPhone, onChange, required, ...props }: VynePhoneProps) => {
    const defaultPlaceholderText = isValidPhone(initialPhone) || initialPhone === "";
    const { updatePhone, shouldUsePhoneInput } = useVynePhoneData({ initialPhone, onChange });

    return shouldUsePhoneInput ? (
        <PhoneInput
            {...props}
            defaultCountry="US"
            onChange={(newPhone) => {
                if (onChange) {
                    // format the phone for validation and use by PhoneInput
                    updatePhone(newPhone ?? "");
                }
            }}
            inputComponent={VyneReactInputANTDBase}
            placeholder={defaultPlaceholderText ? "Enter phone number" : `Previously: ${initialPhone}`}
        />
    ) : (
        <Input
            {...props}
            type="tel"
            onChange={(e) => updatePhone(e.target.value)}
            required={required}
            autoComplete="tel"
        />
    );
};
